import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../utils/api';
import { formatDateWithTime } from '../../../utils/format';

function AdminEmailHistoryView() {
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);

  const [selectedDate, setSelectedDate] = useState('');

  const [messages, setMessages] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await api.get('/auth/users');
        const list = Array.isArray(res?.data?.users) ? res.data.users : [];
        setUsers(list);
      } catch (e) {
        console.error('Failed to fetch users for Email history', e);
      }
    };
    fetchUsers();
  }, []);

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const name = (u.name || '').toLowerCase();
      const email = (u.email || '').toLowerCase();
      const phone = (u.phone || '').toLowerCase();
      const idNumber = (u.idNumber || '').toLowerCase();
      return (
        name.includes(q) ||
        email.includes(q) ||
        phone.includes(q) ||
        idNumber.includes(q)
      );
    });
  }, [userSearch, users]);

  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  const kindLabel = (kind) => {
    if (kind === 'manual_admin') return 'Manual';
    return 'Other';
  };

  const kindColor = (kind) => {
    if (kind === 'manual_admin') return 'bg-sky-500/20 text-sky-300 border-sky-500/30';
    return 'bg-slate-500/20 text-slate-300 border-slate-500/30';
  };

  const resetAndFetch = () => {
    setPage(1);
    fetchMessages(1, selectedUser?._id, selectedDate);
  };

  const fetchMessages = async (pageArg, userIdArg, dateArg) => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams();
      params.append('page', String(pageArg || 1));
      params.append('limit', String(limit));
      if (userIdArg) params.append('userId', userIdArg);
      if (dateArg) params.append('date', dateArg);
      const res = await api.get(`/frp/admin/email-history?${params.toString()}`);
      const data = res?.data || {};
      setMessages(Array.isArray(data.items) ? data.items : []);
      setTotalPages(data.totalPages || 1);
      setTotalItems(data.total || 0);
    } catch (e) {
      console.error('Failed to load Email history', e);
      setError(e?.response?.data?.error || 'Failed to load Email history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages(page, selectedUser?._id, selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleSelectUser = (u) => {
    setSelectedUser(u);
    setUserSearch('');
    setUserDropdownOpen(false);
    setPage(1);
    fetchMessages(1, u?._id, selectedDate);
  };

  const clearFilters = () => {
    setSelectedUser(null);
    setSelectedDate('');
    setUserSearch('');
    setPage(1);
    fetchMessages(1, null, '');
  };

  const handleDateChange = (value) => {
    setSelectedDate(value);
    setPage(1);
    fetchMessages(1, selectedUser?._id, value);
  };

  const handlePageChange = (direction) => {
    if (direction === 'prev' && page > 1) {
      setPage((p) => p - 1);
    } else if (direction === 'next' && page < totalPages) {
      setPage((p) => p + 1);
    }
  };

  return (
    <div
      className="min-h-screen font-sans pb-8 relative overflow-hidden"
      style={{
        background: 'radial-gradient(circle at top left, #0f172a 0%, #020617 45%, #020617 100%)',
      }}
    >
      {/* Background Effects */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-24 -left-10 w-80 h-80 bg-sky-500/15 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-0 w-96 h-96 bg-emerald-500/15 rounded-full blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(148,163,184,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.4) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      {/* Header */}
      <div className="relative z-10 px-4 sm:px-6 lg:px-8 pt-6 pb-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate('/admin')}
            className="group flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all text-slate-200/80 hover:text-white"
          >
            <svg
              className="w-4 h-4 transition-transform group-hover:-translate-x-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-sm font-medium">Dashboard</span>
          </button>
          <button
            onClick={() => navigate('/admin/email')}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-sky-500/20 hover:bg-sky-500/30 border border-sky-500/30 hover:border-sky-500/50 transition-all text-sky-200 hover:text-white"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8" />
            </svg>
            <span className="text-sm font-medium">Compose Email</span>
          </button>
        </div>
      </div>

      {/* Hero */}
      <div className="relative z-10 px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        <div className="max-w-7xl mx-auto flex items-center gap-5">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-sky-500 to-emerald-500 flex items-center justify-center shadow-2xl shadow-sky-500/30">
            <svg className="w-9 h-9 sm:w-10 sm:h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.6}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.6}
                d="M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>
          <div>
            <h1
              className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              Email History
            </h1>
            <p className="text-slate-300/70 text-sm sm:text-base mt-1">
              Review all emails sent to your users, searchable by user and date
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Filters Card */}
        <div
          className="rounded-2xl mb-6 p-5 sm:p-6"
          style={{
            background: 'linear-gradient(180deg, rgba(15,23,42,0.9) 0%, rgba(15,23,42,0.7) 100%)',
            backdropFilter: 'blur(40px)',
            border: '1px solid rgba(148,163,184,0.30)',
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* User Filter */}
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-slate-300/80 mb-2 uppercase tracking-wider">
                Filter by User
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setUserDropdownOpen((o) => !o)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all text-left ${
                    userDropdownOpen
                      ? 'bg-white/10 border-sky-500/60 ring-2 ring-sky-500/25'
                      : 'bg-white/5 hover:bg-white/10 border-white/10 hover:border-white/20'
                  } border`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {selectedUser ? (
                      <>
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-sky-500 to-emerald-500 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                          {getInitials(selectedUser.name)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-white truncate">{selectedUser.name}</p>
                          <p className="text-xs text-slate-300/60 truncate">{selectedUser.email}</p>
                        </div>
                      </>
                    ) : (
                      <span className="text-sm text-slate-300/60">All users</span>
                    )}
                  </div>
                  <div
                    className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all ${
                      userDropdownOpen ? 'bg-sky-500/25 rotate-180' : 'bg-white/5'
                    }`}
                  >
                    <svg className="w-4 h-4 text-slate-200/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>

                {userDropdownOpen && (
                  <div
                    className="absolute z-50 w-full mt-2 rounded-xl overflow-hidden"
                    style={{
                      background: 'linear-gradient(180deg, rgba(15,23,42,0.98) 0%, rgba(15,23,42,0.98) 100%)',
                      backdropFilter: 'blur(40px)',
                      border: '1px solid rgba(148,163,184,0.35)',
                      boxShadow: '0 25px 50px -12px rgba(0,0,0,0.8)',
                    }}
                  >
                    <div className="p-3 border-b border-slate-600/60">
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <svg className="w-4 h-4 text-slate-400/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                          </svg>
                        </div>
                        <input
                          type="text"
                          placeholder="Search users..."
                          value={userSearch}
                          onChange={(e) => setUserSearch(e.target.value)}
                          className="w-full pl-10 pr-4 py-2.5 text-sm bg-slate-900/60 border border-slate-700/80 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500/50 text-slate-100 placeholder-slate-400/50"
                          autoFocus
                        />
                      </div>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {filteredUsers.length === 0 ? (
                        <div className="p-6 text-center text-sm text-slate-400/70">No users found</div>
                      ) : (
                        filteredUsers.slice(0, 80).map((u) => (
                          <button
                            key={u._id}
                            type="button"
                            onClick={() => handleSelectUser(u)}
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-800/80 transition-all text-left group"
                          >
                            <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justifyCenter text-slate-100 text-xs font-semibold flex-shrink-0 group-hover:bg-sky-600 transition-all">
                              {getInitials(u.name)}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-slate-100 truncate group-hover:text-sky-200 transition-colors">
                                {u.name}
                              </p>
                              <p className="text-xs text-slate-400 truncate">{u.email}</p>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Date Filter */}
            <div>
              <label className="block text-xs font-semibold text-slate-300/80 mb-2 uppercase tracking-wider">
                Date
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => handleDateChange(e.target.value)}
                className="w-full px-4 py-3 bg-slate-900/60 border border-slate-700/80 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/60 focus:border-sky-500/60 text-slate-100"
                style={{ colorScheme: 'dark' }}
              />
            </div>

            {/* Actions */}
            <div className="flex items-end gap-2">
              <button
                type="button"
                onClick={resetAndFetch}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-sky-500 to-emerald-500 text-white rounded-xl hover:opacity-95 text-sm font-medium shadow-lg shadow-sky-500/25 transition-all"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                <span>Refresh</span>
              </button>
              <button
                type="button"
                onClick={clearFilters}
                className="px-4 py-3 bg-white/5 hover:bg-white/10 border border-white/10 text-slate-100 rounded-xl text-sm font-medium transition-all"
              >
                Clear
              </button>
            </div>
          </div>

          {/* Summary */}
          <div className="mt-4 pt-4 border-t border-slate-700/70 flex flex-wrap items-center justify-between gap-2">
            <div className="text-xs text-slate-300/70">
              {selectedUser ? (
                <span>
                  Showing emails for <span className="font-semibold text-sky-200">{selectedUser.name}</span>
                  {selectedDate && (
                    <>
                      {' '}
                      on <span className="font-semibold text-sky-200">{selectedDate}</span>
                    </>
                  )}
                </span>
              ) : selectedDate ? (
                <span>
                  Showing emails for <span className="font-semibold text-sky-200">{selectedDate}</span>
                </span>
              ) : (
                <span>Showing all emails</span>
              )}
              <span className="ml-2 px-2 py-0.5 rounded-full bg-white/5 text-slate-200/80 font-medium">
                {totalItems.toLocaleString()} total
              </span>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-xl bg-rose-500/10 border border-rose-500/25 px-4 py-3 text-sm text-rose-200 flex items-center gap-2">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </div>
        )}

        {/* Table */}
        <div
          className="rounded-2xl overflow-hidden"
          style={{
            background: 'linear-gradient(180deg, rgba(15,23,42,0.95) 0%, rgba(15,23,42,0.9) 100%)',
            backdropFilter: 'blur(40px)',
            border: '1px solid rgba(148,163,184,0.35)',
          }}
        >
          {loading ? (
            <div className="py-16 text-center">
              <div className="w-10 h-10 border-2 border-sky-500/40 border-t-sky-500 rounded-full animate-spin mx-auto mb-4" />
              <p className="text-slate-300/70 text-sm">Loading emails...</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="py-16 text-center">
              <div className="w-16 h-16 rounded-2xl bg-slate-800 flex items-center justify-center mx-auto mb-4">
                <svg className="w-7 h-7 text-slate-400/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8" />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.6}
                    d="M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <p className="text-slate-300/70 text-sm">No emails found</p>
              <p className="text-slate-400/50 text-xs mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700/70 bg-slate-900/80">
                    <th className="px-4 sm:px-6 py-3.5 text-left text-xs font-semibold text-slate-300/80 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 sm:px-6 py-3.5 text-left text-xs font-semibold text-slate-300/80 uppercase tracking-wider">
                      Recipient
                    </th>
                    <th className="px-4 sm:px-6 py-3.5 text-left text-xs font-semibold text-slate-300/80 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-4 sm:px-6 py-3.5 text-left text-xs font-semibold text-slate-300/80 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-4 sm:px-6 py-3.5 text-left text-xs font-semibold text-slate-300/80 uppercase tracking-wider">
                      Subject & Preview
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/80">
                  {messages.map((m, idx) => (
                    <tr
                      key={m._id}
                      className="hover:bg-slate-900/80 transition-colors"
                      style={{ animation: `fadeIn 0.3s ease-out ${idx * 0.03}s both` }}
                    >
                      <td className="px-4 sm:px-6 py-3.5 align-top whitespace-nowrap">
                        <span className="text-sm text-slate-100/90">{formatDateWithTime(m.createdAt)}</span>
                      </td>
                      <td className="px-4 sm:px-6 py-3.5 align-top">
                        {m.user ? (
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="hidden sm:flex w-8 h-8 rounded-lg bg-slate-800 items-center justify-center text-slate-100 text-xs font-semibold flex-shrink-0">
                              {getInitials(m.user.name)}
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-slate-100 truncate">{m.user.name}</div>
                              <div className="text-xs text-slate-400 truncate">{m.user.email}</div>
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-slate-400/80">Unknown</span>
                        )}
                      </td>
                      <td className="px-4 sm:px-6 py-3.5 align-top">
                        <div className="text-sm text-sky-300 font-mono truncate max-w-xs sm:max-w-sm">{m.to}</div>
                      </td>
                      <td className="px-4 sm:px-6 py-3.5 align-top">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium border ${kindColor(m.kind)}`}>
                          {kindLabel(m.kind)}
                        </span>
                      </td>
                      <td className="px-4 sm:px-6 py-3.5 align-top w-full">
                        <div className="text-sm text-slate-100 font-medium mb-1 truncate max-w-xs sm:max-w-md">{m.subject}</div>
                        <div className="text-xs text-slate-300/80 line-clamp-2 max-w-xl">
                          {m.text}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {messages.length > 0 && (
            <div className="px-4 sm:px-6 py-4 border-t border-slate-700/70 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="text-xs text-slate-300/70">
                Showing <span className="font-semibold text-slate-100/90">{messages.length}</span> of{' '}
                <span className="font-semibold text-slate-100/90">{totalItems.toLocaleString()}</span> emails
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handlePageChange('prev')}
                  disabled={page <= 1 || loading}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <div className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10">
                  <span className="text-sm font-semibold text-slate-100">{page}</span>
                  <span className="text-sm text-slate-300/60">/</span>
                  <span className="text-sm text-slate-300/80">{totalPages || 1}</span>
                </div>
                <button
                  type="button"
                  onClick={() => handlePageChange('next')}
                  disabled={page >= totalPages || loading}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-white/5 hover:bg白/10 border border-white/10 text-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Extra Styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .max-h-64::-webkit-scrollbar {
          width: 6px;
        }
        .max-h-64::-webkit-scrollbar-track {
          background: rgba(15,23,42,0.8);
        }
        .max-h-64::-webkit-scrollbar-thumb {
          background: rgba(148,163,184,0.8);
          border-radius: 999px;
        }
        .max-h-64::-webkit-scrollbar-thumb:hover {
          background: rgba(148,163,184,1);
        }
      `}</style>
    </div>
  );
}

export default AdminEmailHistoryView;



